import type { Rect, UiRects } from "../../game/input";
import { CANVAS_WIDTH } from "../../game/constants";
import type { RenderModel } from "../renderModel";
import {
  GAME_FONT,
  resolvePhoneMetrics,
  resolvePhoneFrame,
  roundedRectPath,
  type PhoneMetrics,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface NotificationsDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  phoneClosing: boolean;
  phoneCloseMs: number;
  phoneOpenMs: number;
  nowMs: () => number;
  phoneTabIcons: Record<string, HTMLImageElement>;

  drawPhoneMessages(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number, px: number,
    metrics: PhoneMetrics,
  ): void;
  drawPhoneFriends(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
  drawPhoneHighscores(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
  drawPhoneSettings(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
  drawPhoneWallet(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawNotificationsScreen(deps: NotificationsDeps, model: RenderModel): UiRects {
  const { ctx, canvasHeight: canvasH, phoneClosing, phoneCloseMs, phoneOpenMs, nowMs, phoneTabIcons } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const uiRects: UiRects = {};
  const px = 2; // pixel unit for 8-bit feel
  const phoneMetrics = resolvePhoneMetrics();

  // ── 8-Bit Phone Frame ──
  const {
    phoneX,
    phoneY,
    phoneW,
    phoneH,
    cornerR,
    bezelW,
    screenX: scrX,
    screenY: scrY,
    screenW: scrW,
    screenH: scrH,
    contentX,
    contentW,
    contentRight,
    dockY,
    dockH
  } = resolvePhoneFrame(CANVAS_WIDTH, canvasH);

  // Slide-up entrance / slide-down exit animation (300ms)
  let slideOffset: number;
  if (phoneClosing) {
    const closeMs = nowMs() - phoneCloseMs;
    const closeProg = Math.min(1, closeMs / 250);
    const closeEase = closeProg * closeProg; // easeInQuad
    slideOffset = closeEase * (canvasH - phoneY);
  } else {
    const slideMs = nowMs() - phoneOpenMs;
    const slideProg = Math.min(1, slideMs / 300);
    const slideEase = 1 - (1 - slideProg) * (1 - slideProg); // easeOutQuad
    slideOffset = (1 - slideEase) * (canvasH - phoneY);
  }
  ctx.save();
  ctx.translate(0, slideOffset);

  // Phone bounds — clicking outside this dismisses
  uiRects.phoneBounds = { x: phoneX, y: phoneY, w: phoneW, h: phoneH };

  // Outer phone body (dark shell)
  roundedRectPath(ctx, phoneX, phoneY, phoneW, phoneH, cornerR);
  ctx.fillStyle = "#1a2030";
  ctx.fill();
  ctx.strokeStyle = "#3a4a6a";
  ctx.lineWidth = px;
  roundedRectPath(ctx, phoneX, phoneY, phoneW, phoneH, cornerR);
  ctx.stroke();
  ctx.strokeStyle = "#2a3a55";
  ctx.lineWidth = 1;
  roundedRectPath(ctx, phoneX + px, phoneY + px, phoneW - px * 2, phoneH - px * 2, cornerR - 1);
  ctx.stroke();

  roundedRectPath(ctx, scrX, scrY, scrW, scrH, 4);
  ctx.fillStyle = "#0c1624";
  ctx.fill();
  // Scanlines
  ctx.save();
  ctx.globalAlpha = 0.03;
  for (let sy = scrY; sy < scrY + scrH; sy += 3) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(scrX, sy, scrW, 1);
  }
  ctx.restore();

  // ── Status Bar ──
  const statusY = phoneY + bezelW + 2;
  const statusH = 16;
  const d = new Date();
  const timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold 7px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(timeStr, phoneX + bezelW + 6, statusY + statusH / 2);
  const barX = phoneX + phoneW - bezelW - 30;
  const barY = statusY + 4;
  for (let b = 0; b < 4; b++) {
    const bh = 3 + b * 2;
    ctx.fillStyle = "#4dff88";
    ctx.fillRect(barX + b * 5, barY + (10 - bh), 3, bh);
  }
  const batX = phoneX + phoneW - bezelW - 8;
  ctx.fillStyle = "#4dff88";
  ctx.fillRect(batX, barY + 2, 5, 7);
  ctx.fillRect(batX + 1, barY, 3, 2);

  // ── Screen Content (clipped) ──
  ctx.save();
  ctx.beginPath();
  ctx.rect(scrX, scrY, scrW, scrH);
  ctx.clip();

  const tab = model.phoneTab;

  if (tab === "messages") {
    deps.drawPhoneMessages(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, px, phoneMetrics);
  } else if (tab === "friends") {
    deps.drawPhoneFriends(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, phoneMetrics);
  } else if (tab === "highscores") {
    deps.drawPhoneHighscores(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, phoneMetrics);
  } else if (tab === "settings") {
    deps.drawPhoneSettings(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, phoneMetrics);
  } else if (tab === "wallet") {
    deps.drawPhoneWallet(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, phoneMetrics);
  }

  ctx.restore(); // end screen clip

  // ── Bottom Dock (5 tab icons) ──
  ctx.fillStyle = "rgba(10,16,32,0.9)";
  ctx.fillRect(phoneX + bezelW, dockY, scrW, dockH);
  ctx.fillStyle = "#2a3a55";
  for (let dx = phoneX + bezelW; dx < phoneX + phoneW - bezelW; dx += 4) {
    ctx.fillRect(dx, dockY, 2, px);
  }

  const tabColor = "#64d8ff";
  const tabDefs: Array<{ key: string; color: string; label: string; rectKey: keyof UiRects }> = [
    { key: "messages",    color: tabColor, label: "\u2709", rectKey: "phoneTabMessages" },
    { key: "friends",     color: tabColor, label: "\u263A", rectKey: "phoneTabFriends" },
    { key: "highscores",  color: tabColor, label: "\u2605", rectKey: "phoneTabHighscores" },
    { key: "settings",    color: tabColor, label: "\u2699", rectKey: "phoneTabSettings" },
    { key: "wallet",      color: tabColor, label: "\u25C6", rectKey: "phoneTabWallet" },
    { key: "travel",      color: tabColor, label: "\u2708", rectKey: "phoneTabTravel" },
  ];
  const iconCount = tabDefs.length;
  const iconSize = 42;
  const iconGap = 4;
  const totalIconsW = iconCount * iconSize + (iconCount - 1) * iconGap;
  const iconsStartX = scrX + (scrW - totalIconsW) / 2;
  const inactiveAlpha = phoneMetrics.desktop ? 0.1 : 0.15;
  const activeAlpha = phoneMetrics.desktop ? 0.62 : 0.5;
  for (let ic = 0; ic < iconCount; ic++) {
    const td = tabDefs[ic];
    const ix = iconsStartX + ic * (iconSize + iconGap);
    const iy = dockY + (dockH - iconSize) / 2;
    const isActive = tab === td.key;
    // Icon bg
    roundedRectPath(ctx, ix, iy, iconSize, iconSize, 4);
    ctx.fillStyle = td.color;
    ctx.globalAlpha = isActive ? activeAlpha : inactiveAlpha;
    ctx.fill();
    ctx.globalAlpha = 1;
    roundedRectPath(ctx, ix, iy, iconSize, iconSize, 4);
    ctx.strokeStyle = td.color;
    ctx.lineWidth = isActive ? (phoneMetrics.desktop ? 2.2 : 2) : 1;
    ctx.stroke();
    // Active indicator dot/bar
    if (isActive) {
      ctx.fillStyle = td.color;
      if (phoneMetrics.desktop) {
        ctx.fillRect(ix + 7, iy + iconSize + 2, iconSize - 14, 3);
      } else {
        ctx.beginPath();
        ctx.arc(ix + iconSize / 2, iy + iconSize + 3, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // Icon symbol
    const tabIcon = phoneTabIcons[td.key];
    if (tabIcon != null) {
      const pad = 2;
      ctx.drawImage(tabIcon, ix + pad, iy + pad, iconSize - pad * 2, iconSize - pad * 2);
    } else {
      ctx.fillStyle = isActive ? "#ffffff" : td.color;
      ctx.font = `bold ${phoneMetrics.desktop ? 16 : 17}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(td.label, ix + iconSize / 2, iy + iconSize / 2);
    }
    // Register rect
    (uiRects as Record<string, Rect>)[td.rectKey] = { x: ix, y: iy, w: iconSize, h: iconSize };
  }

  // ── Home button ──
  const homeBtnSize = 10;
  const homeBtnX = centerX - homeBtnSize / 2;
  const homeBtnY = phoneY + phoneH - bezelW / 2 - homeBtnSize / 2 + 1;
  roundedRectPath(ctx, homeBtnX, homeBtnY, homeBtnSize, homeBtnSize, 3);
  ctx.strokeStyle = "#3a4a6a";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore(); // end slide-up transform

  return uiRects;
}
